// /user-dashboard/ai-site: the website editor for a prompt-built site, or
// the build flow when there isn't one yet (or ?new=1, "start over").
import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import Loader from "../../components/loaders/loader";
import { API_BASE_URL } from "../../store/baseQuery";
import { useCreateTemplateMutation, useGetTemplateQuery, useSaveDraftMutation } from "../../store/Reducer/templateApis";
import { toBackendTemplateId } from "../../utils/templateIdMap";
import AiSiteEditor from "./AiSiteEditor";
import BuildWizard from "./BuildWizard";
import { AI_SITE_KEY, isAiSite } from "./siteModel";

const hasSettings = (map) => map && typeof map === "object" && Object.keys(map).length > 0;

const AiSiteBuilder = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { data, error, isFetching, refetch } = useGetTemplateQuery(undefined, { refetchOnMountOrArgChange: true });
  const [createTemplate] = useCreateTemplateMutation();
  const [saveDraft] = useSaveDraftMutation();
  // A site just built in this visit, already saved as the draft.
  const [built, setBuilt] = useState(null);

  if (error && error.status !== 404) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 p-6 text-center">
        <p className="text-slate-800">Couldn't load your website.</p>
        <button type="button" onClick={refetch} className="rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-white">Try again</button>
      </div>
    );
  }
  if (isFetching && !built) return <div className="flex min-h-screen items-center justify-center"><Loader /></div>;

  const doc = error ? null : data?.data;
  const draft = isAiSite(doc?.draftMap) ? doc.draftMap : null;
  const live = isAiSite(doc?.templateMap) ? doc.templateMap : null;
  const liveUrl = doc?.templateUrlPath && hasSettings(doc.templateMap) ? `${API_BASE_URL}/sites/${doc.templateUrlPath}` : null;
  const startOver = params.get("new") === "1";

  const saveBuilt = async (site) => {
    try {
      // Drafts live on the masjid's site record. Create it if this is the
      // masjid's first website; an existing one keeps its live site.
      if (!doc) await createTemplate({ templateId: toBackendTemplateId(AI_SITE_KEY), templateMap: {} }).unwrap();
      const res = await saveDraft({ templateMap: site, templateId: toBackendTemplateId(AI_SITE_KEY) }).unwrap();
      setBuilt(res?.data?.templateMap || site);
      navigate("/user-dashboard/ai-site", { replace: true });
    } catch (err) {
      toast.error(err?.data?.message || "Couldn't save your new website. Try again.");
    }
  };

  const site = built || (!startOver && (draft || live));
  if (site) {
    return (
      <AiSiteEditor
        initialSite={site}
        startsAsDraft={Boolean(built || draft)}
        liveUrl={liveUrl}
        onStartOver={() => { setBuilt(null); navigate("/user-dashboard/ai-site?new=1"); }}
        onDiscarded={async () => {
          setBuilt(null);
          const { data: fresh } = await refetch();
          if (!isAiSite(fresh?.data?.templateMap)) navigate("/user-dashboard/masjid-web");
        }}
      />
    );
  }

  return (
    <BuildWizard
      initialPrompt={params.get("prompt") || ""}
      hasDraft={hasSettings(doc?.draftMap)}
      hasLiveSite={hasSettings(doc?.templateMap)}
      onBuilt={saveBuilt}
      onCancel={() => navigate(draft || live ? "/user-dashboard/ai-site" : "/user-dashboard/masjid-web")}
    />
  );
};

export default AiSiteBuilder;
